(function () {

  const IMMUNITY_KEY = '549FFD6F-D51F-4AAA-B4FF-37A567B6E441';

  /**
   * Trait definitions.
   * Each entry describes the default duration and which stats are saved/restored.
   * Duration of 9999 = indefinite (rooted, bleeding).
   */
  const DEFINITIONS = {

    // ── DAMAGE OVER TIME ─────────────────────────────────────────────────────
    burning: {
      duration: 3,
      category: 'dot',
      label: 'Burning',
      description: 'Takes 800 damage at the start of each turn (ignores armor).',
    },
    poisoned: {
      duration: 4,
      category: 'dot',
      label: 'Poisoned',
      description: 'Takes 450 damage at the end of each turn. Healing reduced by 75% while active.',
    },
    bleeding: {
      duration: 9999,
      category: 'dot',
      label: 'Bleeding',
      description: 'Takes 300 damage each time the card acts or is hit (ignores armor).',
    },
    withered: {
      duration: 3,
      category: 'dot',
      label: 'Withered',
      description: 'Takes 667 damage at the start of each turn for 3 turns (≈2000 total). Non-lethal (min 1 HP).',
    },

    // ── CONTROL / TEMPO ───────────────────────────────────────────────────────
    slowed: {
      duration: 3,
      category: 'control',
      label: 'Slowed',
      description: 'Speed reduced by 50%.',
    },
    frozen: {
      duration: 3,
      category: 'control',
      label: 'Frozen',
      description: 'Cannot act. Critical hits break Frozen early.',
    },
    rooted: {
      duration: 9999,
      category: 'control',
      label: 'Rooted',
      description: 'Cannot gain Speed bonuses. Speed is capped at its current value.',
    },
    staggered: {
      duration: 4,
      category: 'control',
      label: 'Staggered',
      description: 'Accuracy reduced by 35%.',
    },
    pacified: {
      duration: 4,
      category: 'control',
      label: 'Pacified',
      description: 'Attack reduced by 20%, cannot use offensive abilities, cannot inflict DoT traits.',
    },

    // ── DISRUPTION ────────────────────────────────────────────────────────────
    shocked: {
      duration: 3,
      category: 'disruption',
      label: 'Shocked',
      description: 'Accuracy and Speed reduced by 20%. Ability disabled.',
    },
    overheated: {
      duration: 3,
      category: 'disruption',
      label: 'Overheated',
      description: 'Resource drain +5 per attack. Critical hits disabled.',
    },
    hacked: {
      duration: 3,
      category: 'disruption',
      label: 'Hacked',
      description: '20% chance each turn for the enemy to use your ability against you.',
    },

    // ── MENTAL / MAGIC ────────────────────────────────────────────────────────
    confused: {
      duration: 3,
      category: 'mental',
      label: 'Confused',
      description: '20% chance to attack self at 50% power each turn instead of the enemy.',
    },
    dominated: {
      duration: 3,
      category: 'mental',
      label: 'Dominated',
      description: 'Ward reduced by 20%. Cannot use defensive abilities. Cannot apply mental/magic traits (Confused, Cursed, Hallucinating, Dominated).',
    },
    hallucinating: {
      duration: 4,
      category: 'mental',
      label: 'Hallucinating',
      description: '5% chance each turn to attack the next waiting teammate instead of the enemy.',
    },
    cursed: {
      duration: 4,
      category: 'mental',
      label: 'Cursed',
      description: 'Healing and stat buffs are disabled. Double-cursed = permanent loss of both effects.',
    },

    // ── DEFENCE BREAK ─────────────────────────────────────────────────────────
    broken: {
      duration: 4,
      category: 'defence',
      label: 'Broken',
      description: 'Armor reduced by 50%.',
    },
    disarmed: {
      duration: 3,
      category: 'defence',
      label: 'Disarmed',
      description: 'Physical attack reduced by 25%. Cannot inflict Bleeding.',
    },
    marked: {
      duration: 4,
      category: 'defence',
      label: 'Marked',
      description: 'Accuracy of attackers is effectively +75%. Negative trait chance doubled.',
    },
  };

  // ─── DB ID → slug map (populated by loadFromDb) ──────────────────────────────
  const _traitIdToSlug = {};

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  function hasImmunity(card) {
    return card.abilityKey === IMMUNITY_KEY;
  }

  function isDoTTrait(traitName) {
    return ['burning', 'poisoned', 'bleeding', 'withered'].indexOf(traitName) !== -1;
  }

  // ─── Public API ──────────────────────────────────────────────────────────────

  window.traits = {

    definitions: DEFINITIONS,

    /**
     * Populate the DB-ID → slug map from a traits API response.
     * Call once at startup (after traitService.get() resolves).
     * @param {Array} traitsData  Array of { id, name, ... } from the database.
     */
    loadFromDb: function (traitsData) {
      traitsData.forEach(function (record) {
        const slug = Object.keys(DEFINITIONS).find(function (key) {
          return DEFINITIONS[key].label.toLowerCase() === record.name.toLowerCase();
        });
        if (slug && record.id) {
          _traitIdToSlug[record.id] = slug;
        }
      });
    },

    /**
     * Resolve a database trait ID to its internal slug key.
     * Returns null if the ID is unknown.
     * @param {string} traitId  UUID from the database.
     */
    resolveSlug: function (traitId) {
      return _traitIdToSlug[traitId] || null;
    },

    /**
     * Apply a trait to a card.
     * Saves original stat values before modifying them so they can be restored on expiry.
     * Skips application if card has immunity.
     */
    apply: function (card, traitName) {
      if (hasImmunity(card)) { return; }
      // If traitName is a DB UUID, resolve it to the slug first
      if (!DEFINITIONS[traitName] && _traitIdToSlug[traitName]) {
        traitName = _traitIdToSlug[traitName];
      }
      if (!DEFINITIONS[traitName]) { return; }

      const def = DEFINITIONS[traitName];
      const existing = card.activeTraits && card.activeTraits[traitName];

      // If trait already active, refresh duration (do not re-apply stat changes)
      if (existing) {
        existing.turnsLeft = def.duration;
        console.log(`[Traits] ${traitName} refreshed on ${card.name} (${def.duration} turns)`);
        return;
      }

      // Ensure activeTraits exists
      if (!card.activeTraits) { card.activeTraits = {}; }

      const entry = { turnsLeft: def.duration, label: def.label };

      // ── Vigil — mental immunity guard ────────────────────────────────────────
      // If the target has _vigilMentalImmune, block any mental-category trait
      // from being applied. Set _vigilMentalBlockedThisTick so the passive can
      // increment ward / mAtk stacks this tick. Return before the trait is added
      // to activeTraits so traits.has() stays false for behavioural checks.
      if (card._vigilMentalImmune && def.category === 'mental') {
        card._vigilMentalBlockedThisTick = true;
        return;
      }

      // ── Apply stat modifications and save originals ──────────────────────────
      switch (traitName) {

        case 'slowed':
          entry.savedSpeed = card.speed;
          entry.savedPAtk  = card.pAtk;
          entry.savedMAtk  = card.mAtk;
          if (card._slowedImmune) {
            // Parallax — slowed is converted into a buff
            card.speed = Math.round(card.speed * 1.30); // +30% speed
            card.pAtk  = Math.round(card.pAtk  * 1.10); // +10% pAtk
            card.mAtk  = Math.round(card.mAtk  * 1.10); // +10% mAtk
          } else if (card._throttleSlowImmune) {
            // Throttle — slowed is fully ineffective; no stat changes, no save needed
            entry.savedSpeed = undefined;
            entry.savedPAtk  = undefined;
            entry.savedMAtk  = undefined;
          } else {
            card.speed = Math.round(card.speed * 0.5);
          }
          break;

        case 'staggered':
          entry.savedAccuracy = card.accuracy;
          card.accuracy = Math.round(card.accuracy * 0.65);
          break;

        case 'pacified':
          entry.savedAttack = card.attack;
          entry.savedPAtk = card.pAtk;
          card.attack = Math.round(card.attack * 0.8);
          card.pAtk = Math.round(card.pAtk * 0.8);
          break;

        case 'shocked':
          entry.savedAccuracy = card.accuracy;
          entry.savedSpeed = card.speed;
          entry.savedAbilityKey = card.abilityKey;
          card.accuracy = Math.round(card.accuracy * 0.8);
          card.speed = Math.round(card.speed * 0.8);
          // card._shockNullsAbility === false means the card's passive counters shocked
          // (e.g. Emperor Palpatine) — preserve abilityKey so their passive can still fire
          if (card._shockNullsAbility !== false) { card.abilityKey = null; }
          break;

        case 'withered':
          // No immediate stat change — damage is dealt per turn in processStartOfTurn
          break;

        case 'broken':
          entry.savedArmor = card.armor;
          entry.savedDefence = card.defence;
          card.armor = Math.round(card.armor * 0.5);
          card.defence = Math.round(card.defence * 0.5);
          break;

        case 'dominated':
          entry.savedWard = card.ward;
          card.ward = Math.round(card.ward * 0.8);
          break;

        case 'disarmed':
          entry.savedPAtk = card.pAtk;
          entry.savedAttack = card.attack;
          card.pAtk = Math.round(card.pAtk * 0.75);
          card.attack = Math.round(card.attack * 0.75);
          break;

        case 'hallucinating':
          entry.savedAccuracy = card.accuracy;
          card.accuracy = Math.round(card.accuracy * 0.9);
          break;
      }

      card.activeTraits[traitName] = entry;
      console.log(`[Traits] ${traitName} applied to ${card.name} | duration: ${def.duration >= 9999 ? '∞' : def.duration + ' turns'} | category: ${def.category}`, entry);
    },

    /**
     * Remove a trait from a card and restore any saved stats.
     */
    remove: function (card, traitName) {
      if (!card.activeTraits || !card.activeTraits[traitName]) { return; }

      const entry = card.activeTraits[traitName];

      switch (traitName) {

        case 'slowed':
          if (entry.savedSpeed !== undefined) { card.speed = entry.savedSpeed; }
          if (entry.savedPAtk  !== undefined) { card.pAtk  = entry.savedPAtk; }
          if (entry.savedMAtk  !== undefined) { card.mAtk  = entry.savedMAtk; }
          break;

        case 'staggered':
          if (entry.savedAccuracy !== undefined) { card.accuracy = entry.savedAccuracy; }
          break;

        case 'pacified':
          if (entry.savedAttack !== undefined) { card.attack = entry.savedAttack; }
          if (entry.savedPAtk !== undefined) { card.pAtk = entry.savedPAtk; }
          break;

        case 'shocked':
          if (entry.savedAccuracy !== undefined) { card.accuracy = entry.savedAccuracy; }
          if (entry.savedSpeed !== undefined) { card.speed = entry.savedSpeed; }
          if (entry.savedAbilityKey !== undefined) { card.abilityKey = entry.savedAbilityKey; }
          break;

        case 'withered':
          // Nothing to restore — damage was dealt per turn
          break;

        case 'broken':
          if (entry.savedArmor !== undefined) { card.armor = entry.savedArmor; }
          if (entry.savedDefence !== undefined) { card.defence = entry.savedDefence; }
          break;

        case 'disarmed':
          if (entry.savedPAtk !== undefined) { card.pAtk = entry.savedPAtk; }
          if (entry.savedAttack !== undefined) { card.attack = entry.savedAttack; }
          break;

        case 'dominated':
          if (entry.savedWard !== undefined) { card.ward = entry.savedWard; }
          break;

        case 'hallucinating':
          if (entry.savedAccuracy !== undefined) { card.accuracy = entry.savedAccuracy; }
          break;
      }

      delete card.activeTraits[traitName];
    },

    /**
     * Check whether a card currently has a given trait.
     */
    has: function (card, traitName) {
      return !!(card.activeTraits && card.activeTraits[traitName]);
    },

    /**
     * Decrement all active trait durations by 1 and remove any that have expired.
     * Call once per round after all attacks are resolved.
     */
    tick: function (card) {
      if (!card.activeTraits) { return; }

      for (const traitName of Object.keys(card.activeTraits)) {
        const entry = card.activeTraits[traitName];

        // Indefinite traits (bleeding, rooted) use duration 9999 — never tick off
        if (entry.turnsLeft >= 9999) { continue; }

        entry.turnsLeft -= 1;

        if (entry.turnsLeft <= 0) {
          traits.remove(card, traitName);
        }
      }
    },

    /**
     * Returns true if the card cannot attack this turn (frozen or pacified offensive block).
     * Note: pacified blocks offensive abilities but still allows the card to perform basic attacks.
     * Only frozen fully prevents acting.
     */
    isBlocked: function (card) {
      return traits.has(card, 'frozen');
    },

    /**
     * Returns true if the attacker is allowed to apply DoT traits.
     * Pacified attackers cannot inflict burning, poisoned, or bleeding.
     */
    canApplyDot: function (attackerCard) {
      return !traits.has(attackerCard, 'pacified');
    },

    /**
     * Returns false if the attacker is dominated — cannot apply mental/magic traits
     * (confused, cursed, hallucinating, dominated).
     */
    canApplyMentalTrait: function (attackerCard) {
      return !traits.has(attackerCard, 'dominated');
    },

    /**
     * Returns a boosted application chance multiplier for negative traits.
     * Marked targets are 1.5× more likely to receive a negative trait.
     * Abilities that apply traits should multiply their random roll threshold by this value.
     */
    negativeChanceMult: function (targetCard) {
      return traits.has(targetCard, 'marked') ? 1.5 : 1.0;
    },

    // ─── Damage / Stat Modifiers ────────────────────────────────────────────────

    /**
     * Physical damage output multiplier for the attacker.
     * Disarmed: ×0.75 already applied to pAtk on trait application — no extra mult needed here.
     * This modifier is for further runtime adjustments.
     */
    getPhysicalDamageMult: function (card) {
      return 1.0; // pAtk already modified on apply for disarmed/pacified
    },

    /**
     * Magic damage output multiplier for the attacker.
     * Dominated: -20% magic damage output.
     */
    getMagicDamageMult: function (card) {
      if (traits.has(card, 'dominated')) { return 0.8; }
      return 1.0;
    },

    /**
     * Extra resource cost per attack (overheated: +5 stamina/mana).
     */
    getExtraResourceCost: function (card) {
      if (traits.has(card, 'overheated')) { return 5; }
      return 0;
    },

    /**
     * Whether critical hits should be suppressed (overheated).
     */
    blocksCriticalHit: function (card) {
      return traits.has(card, 'overheated');
    },

    /**
     * Whether the card blocks incoming healing (cursed).
     * Call this before any HP restoration to decide whether to allow it.
     */
    blocksHealing: function (card) {
      return traits.has(card, 'cursed');
    },

    /**
     * Whether the card blocks stat buffs (cursed).
     */
    blocksBuffs: function (card) {
      return traits.has(card, 'cursed');
    },

    /**
     * Whether the card blocks its own offensive ability from firing (pacified).
     */
    blocksOffensiveAbility: function (card) {
      return traits.has(card, 'pacified');
    },

    /**
     * Whether the card blocks its own defensive ability from firing (dominated).
     */
    blocksDefensiveAbility: function (card) {
      return traits.has(card, 'dominated');
    },

    // ─── Processing Hooks ───────────────────────────────────────────────────────

    /**
     * Fire start-of-turn trait effects.
     * Called once per round for the active card before attacks begin.
     *
     * @param {Object} card         The active card whose start-of-turn fires
     * @param {Array}  enemyCards   The enemy team array (for marked dodge calc)
     * @param {number} enemyIndex   Current enemy index
     * @param {Function} logFn      battleArena's logEvent(message, type, meta)
     */
    processStartOfTurn: function (card, enemyCards, enemyIndex, logFn) {
      if (!card.activeTraits) { return; }

      // Burning — ignores armor, fires at start of turn
      if (traits.has(card, 'burning')) {
        if (card._burningImmune) {
          // Scorpion — Infernal Resolve: burning is ineffective (no damage dealt)
          if (typeof effects !== 'undefined') { effects.add(card, ['glow-red'], 1000); }
          if (logFn) { logFn(`${card.name}'s Infernal Resolve: burning is ineffective!`, 'ability', { tier: card.tier }); }
        } else {
          const dmg = 800;
          card.hp = Math.max(0, card.hp - dmg);
          if (typeof effects !== 'undefined') { effects.add(card, ['glow-red'], 1000); }
          if (typeof window.battleUI !== 'undefined') { window.battleUI.showHpLost(card, dmg, 'burning'); }
          if (logFn) { logFn(`${card.name} takes ${dmg} burning damage!`, 'damage', { tier: card.tier }); }
        }
      }

      // Withered — 667 damage/turn for 3 turns (≈2000 total), non-lethal
      if (traits.has(card, 'withered')) {
        const dmg = 667;
        card.hp = Math.max(1, card.hp - dmg);
        if (typeof effects !== 'undefined') { effects.add(card, ['wobble'], 800); }
        if (typeof window.battleUI !== 'undefined') { window.battleUI.showHpLost(card, dmg, 'withered'); }
        if (logFn) { logFn(`${card.name} withers for ${dmg} damage!`, 'damage', { tier: card.tier }); }
      }

      // Frozen — announce the card is frozen (attack block is handled in attemptAttack via isBlocked)
      if (traits.has(card, 'frozen')) {
        if (typeof effects !== 'undefined') { effects.add(card, ['blur'], 800); }
        if (logFn) { logFn(`${card.name} is Frozen and cannot act!`, 'skip', { tier: card.tier }); }
      }
    },

    /**
     * Fire end-of-turn trait effects.
     * Called after all attacks in a round are complete.
     *
     * @param {Object}   card   The active card
     * @param {Function} logFn  battleArena's logEvent
     */
    processEndOfTurn: function (card, logFn) {
      if (!card.activeTraits) { return; }

      // Poisoned — fires at end of turn
      if (traits.has(card, 'poisoned')) {
        const dmg = 450;
        if (card._poisonConvertsToHeal) {
          // Plague Doctor — Antidote: poison ticks heal instead of damage
          const maxHp = card.originalHp || card.hp;
          card.hp = Math.min(maxHp, card.hp + dmg);
          if (typeof effects !== 'undefined') { effects.add(card, ['glow-green'], 900); }
          if (logFn) { logFn(`${card.name}'s Antidote: poison converted to +${dmg} HP!`, 'ability', { tier: card.tier }); }
        } else if (card._poisonImmune) {
          // Stark — Toxin Mastery: immune to poison; drain reversed onto current opponent
          const opponent = card._starkPoisonReverseTarget;
          if (opponent && opponent.hp > 0) {
            opponent.hp = Math.max(0, opponent.hp - dmg);
            if (typeof effects !== 'undefined') { effects.add(opponent, ['wobble'], 900); }
            if (typeof window.battleUI !== 'undefined') { window.battleUI.showHpLost(opponent, dmg, 'poisoned'); }
            if (logFn) { logFn(`${card.name}'s Toxin Mastery: poison reversed — ${opponent.name} takes ${dmg} damage!`, 'ability', { tier: card.tier }); }
          }
        } else {
          card.hp = Math.max(0, card.hp - dmg);
          if (typeof effects !== 'undefined') { effects.add(card, ['wobble'], 900); }
          if (typeof window.battleUI !== 'undefined') { window.battleUI.showHpLost(card, dmg, 'poisoned'); }
          if (logFn) { logFn(`${card.name} takes ${dmg} poison damage!`, 'damage', { tier: card.tier }); }
        }
      }
    },

    /**
     * Fire on-act trait effects (when the card performs an action/attack).
     * Bleeding fires here.
     *
     * @param {Object}   card   The card that is acting
     * @param {Function} logFn  battleArena's logEvent
     */
    processOnAct: function (card, logFn) {
      if (!card.activeTraits) { return; }

      // Bleeding — fires each time card acts (ignores armor)
      if (traits.has(card, 'bleeding')) {
        const dmg = Math.round(300 * (card._bleedDmgMult || 1));
        card.hp = Math.max(0, card.hp - dmg);
        if (typeof effects !== 'undefined') { effects.add(card, ['glow-red'], 700); }
        if (typeof window.battleUI !== 'undefined') { window.battleUI.showHpLost(card, dmg, 'bleeding'); }
        if (logFn) { logFn(`${card.name} bleeds for ${dmg} damage!`, 'damage', { tier: card.tier }); }
      }
    },

    /**
     * Fire on-hit trait effects (when the card is hit by an attack).
     * Bleeding also fires here. Critical hits break Frozen.
     *
     * @param {Object}   card        The card that was hit
     * @param {number}   damage      The damage dealt this hit
     * @param {boolean}  isCritical  Whether the hit was a critical
     * @param {Function} logFn       battleArena's logEvent
     */
    processOnHit: function (card, damage, isCritical, logFn) {
      if (!card.activeTraits) { return; }

      // Frozen — critical hits break Frozen immediately
      if (isCritical && traits.has(card, 'frozen')) {
        traits.remove(card, 'frozen');
        if (typeof effects !== 'undefined') { effects.add(card, ['tada'], 800); }
        if (logFn) { logFn(`${card.name}'s Frozen status was broken by a critical hit!`, 'ability', { tier: card.tier }); }
      }

      // Bleeding — fires each time card is hit (ignores armor)
      if (traits.has(card, 'bleeding')) {
        const dmg = Math.round(300 * (card._bleedDmgMult || 1));
        card.hp = Math.max(0, card.hp - dmg);
        if (typeof effects !== 'undefined') { effects.add(card, ['glow-red'], 700); }
        if (typeof window.battleUI !== 'undefined') { window.battleUI.showHpLost(card, dmg, 'bleeding'); }
        if (logFn) { logFn(`${card.name} bleeds for ${dmg} damage!`, 'damage', { tier: card.tier }); }
      }
    },

  };

})();
